import ptCoreClient from 'plant-core-client';

import router from '@/router';
import {
	ACTION_API_LOGIN,
	ACTION_GET_USER_PERMISSIONS,
	ACTION_LOGOUT,
	ACTION_REPORT_LOGIN_ERROR,
	GETTER_USER_PERMISSIONS,
	MUTATION_DESTROY_SESSION,
	MUTATION_LOGIN_ERROR,
	MUTATION_LOGIN_PENDING,
	MUTATION_SET_LOGIN_DATA,
	MUTATION_SET_USER_PERMISSIONS,
	STATE_PENDING,
	STATE_PROFILE,
	STATE_TOKEN,
	STATE_USER_PERMISSONS,
	STATE_VERSION_INFO,
	STORE_USER_NAME
} from '@/store_constants/user';
import {
	ACTION_NOTIFICATIONS_ERROR,
	NOTIFICATION_INFINITE_TIMEOUT,
	STORE_NOTIFICATIONS_NAME
} from '@/store_constants/notifications';
import { AuthService } from '@/services/AuthService';
import { MUTATION_SET_USER_ROLE, STORE_ROLES_NAME } from '@/store_constants/roles';
import { ACTION_LEAVE_PLANT, STORE_LIBRARY_NAME } from '@/store_constants/library';

ptCoreClient.baseURL = window.__env__.VUE_APP_FLEET_API_ENDPOINT;

function getMessageFromError(error) {
	if (error.response && error.response.data) {
		return error.response.data.message;
	}

	return 'Unexpected error';
}

export default {
	namespaced: true,
	name: STORE_USER_NAME,
	state: {
		[STATE_TOKEN]: '',
		[STATE_VERSION_INFO]: [],
		[STATE_PROFILE]: false,
		[STATE_PENDING]: false,
		[STATE_USER_PERMISSONS]: []
	},
	getters: {
		[GETTER_USER_PERMISSIONS](state) {
			return state[STATE_USER_PERMISSONS];
		}
	},
	mutations: {
		[MUTATION_SET_LOGIN_DATA](state, { profile, token }) {
			state[STATE_TOKEN] = token;
			state[STATE_PROFILE] = profile;
			state[STATE_PENDING] = false;
			state[STATE_VERSION_INFO] = [
				{
					label: 'Build date: ',
					info: process.env.VUE_APP_BUILD
				},
				{
					label: 'Frontend Version: ',
					info: process.env.VUE_APP_VERSION
				}
			];
		},
		[MUTATION_DESTROY_SESSION](state) {
			state[STATE_TOKEN] = '';
			state[STATE_PROFILE] = false;
			state[STATE_PENDING] = false;
			state[STATE_VERSION_INFO] = [];
			router.push('/login');
		},
		[MUTATION_LOGIN_PENDING](state) {
			state[STATE_PENDING] = true;
		},
		[MUTATION_LOGIN_ERROR](state) {
			state[STATE_PENDING] = false;
		},
		[MUTATION_SET_USER_PERMISSIONS](state, permissions) {
			state[STATE_USER_PERMISSONS] = permissions;
		}
	},
	actions: {
		[ACTION_API_LOGIN]({ commit, dispatch }, credentials) {
			const authService = new AuthService();
			return authService
				.login(credentials)
				.then(loginResult => {
					commit(MUTATION_SET_LOGIN_DATA, loginResult);
					dispatch('notifications/info', { message: 'Login Successful' }, { root: true });
					return loginResult;
				})
				.catch(error => {
					dispatch(ACTION_REPORT_LOGIN_ERROR, `PLANT Login Error: ${getMessageFromError(error)}`);
					throw error;
				});
		},
		[ACTION_REPORT_LOGIN_ERROR]({ commit, dispatch }, errorMessage) {
			commit(MUTATION_DESTROY_SESSION);
			dispatch(
				'notifications/error',
				{
					message: errorMessage,
					timeout: NOTIFICATION_INFINITE_TIMEOUT
				},
				{ root: true }
			);
		},
		[ACTION_LOGOUT]({ commit, dispatch, state }) {
			if (!state[STATE_TOKEN]) return;
			const authService = new AuthService(state[STATE_TOKEN]);
			authService
				.logout()
				.then({})
				.catch(error => {
					dispatch(
						`${STORE_NOTIFICATIONS_NAME}/${ACTION_NOTIFICATIONS_ERROR}`,
						{
							message: `An error occurred while trying to logout: ${error.message}`,
							timeout: NOTIFICATION_INFINITE_TIMEOUT
						},
						{ root: true }
					);
					return error;
				});
			commit(MUTATION_DESTROY_SESSION);
			dispatch(`${STORE_LIBRARY_NAME}/${ACTION_LEAVE_PLANT}`, {}, { root: true });
		},
		async [ACTION_GET_USER_PERMISSIONS]({ commit, dispatch, state }) {
			const authService = new AuthService(state[STATE_TOKEN]);
			return authService
				.whoami()
				.then(res => {
					const userPermissions = res.role.permissions;
					commit(MUTATION_SET_USER_PERMISSIONS, userPermissions);
					const userRole = res.role;
					commit(`${STORE_ROLES_NAME}/${MUTATION_SET_USER_ROLE}`, userRole, { root: true });
				})
				.catch(error => {
					const errorStatus = error.response.status;
					if (errorStatus !== 401) {
						dispatch(
							`${STORE_NOTIFICATIONS_NAME}/${ACTION_NOTIFICATIONS_ERROR}`,
							{ message: 'An error occurred while trying to get your profile information' },
							{ root: true }
						);
					}

					return error;
				});
		}
	}
};
